import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Translate from "../../utils/Translate";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../utils/Api";
import { useLocation, useNavigate } from "react-router-dom";
import Preloader from "../Preloader";
import { DataGridPro as DataGrid, useGridApiRef } from "@mui/x-data-grid-pro";
import { dataGridLang } from "../MemToolBar";
import { ToolBar } from "../ToolBar";
import { IconAndLabelColumnHeader } from "../../utils/dataGrid";
import {
  Clear,
  CropFree,
  ExpandMore,
  Inventory,
  Numbers,
  ScatterPlot,
  ShoppingBasket,
  ViewInAr,
  Warning,
} from "@mui/icons-material";
import DotMenu from "../DotMenu";
import { CustomNoRowsOverlay } from "../../utils/StyledGridOverlay";
import { AlertDialog } from "../AlertDialog";
import SelectMultiPartChildStUnitDialog from "./SelectMultiPartChildStUnitDialog";
import _ from "lodash";
import StUnitField from "../orders/StUnitField";

export default function AssembleMultiPartForm({ partId, onClose, onSaved, defaultQty }) {
  const [part, setPart] = useState({});
  const [multiPartPickAreaId, setMultiPartPickAreaId] = useState(null);
  const [qtyToAssemble, setQtyToAssemble] = useState(defaultQty ?? 1);
  const [stUnitsPerChild, setStUnitsPerChild] = useState({});
  const [selectedAreaIdPerChild, setSelectedAreaIdPerChild] = useState({});
  const [selectedStUnitsPerChild, setSelectedStUnitsPerChild] = useState({});
  const [availableQtyInAreaPerChild, setAvailableQtyInAreaPerChild] = useState({});
  const [plannedPickSummaryPerChild, setPlannedPickSummaryPerChild] = useState({});
  const [areas, setAreas] = useState([]);
  const [showSelectStUnitDialogForChild, setShowSelectStUnitDialogForChild] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const api = useApi();
  const apiRef = useGridApiRef();
  const location = useLocation();
  const navigate = useNavigate();
  const localizedTextsMap = dataGridLang();

  useEffect(() => {
    async function init() {
      setIsLoading(true);

      const partResponse = await loadPart(partId, api);
      const areas = await loadAreas(api);
      const sortedAreas = areas ? [...areas].sort((a, b) => a.shortName.localeCompare(b.shortName)) : areas;
      const firstArea = sortedAreas && sortedAreas[0];
      const selectedAreas = Object.fromEntries(partResponse.children.map((ch) => [ch.child.partId, firstArea.areaId]));
      const stUnits = Object.fromEntries(partResponse.children.map((ch) => [ch.child.partId, ch.child.stUnits]));

      setPart(partResponse);
      setMultiPartPickAreaId(partResponse.pickAreaId ?? firstArea.areaId);
      setAreas(sortedAreas ?? []);
      setSelectedAreaIdPerChild(selectedAreas);
      setStUnitsPerChild(stUnits);
      setIsLoading(false);
    }
    init();
  }, [api]);

  // Update availableQtyInAreaPerChild
  useEffect(() => {
    const hasUpdatedState = part.partId;
    if (!hasUpdatedState) {
      return;
    }

    const childAreaSummaryEntries = part.children.map(({ child }) => {
      const stUnits = stUnitsPerChild[child.partId] ?? [];
      const availableQtyPerArea = areas.map((area) => {
        const stUnitsInArea = stUnits.filter((st) => st.locationAreaId == area.areaId);
        const availableQtyInArea = stUnitsInArea.map((st) => st.qty).reduce((acc, qty) => acc + qty, 0);

        const selectedStUnits = selectedStUnitsPerChild[child.partId] ?? [];
        const selectedStUnitsInArea = selectedStUnits.filter((st) => st.locationAreaId == area.areaId);
        const reservedQtyInArea = selectedStUnitsInArea.map((st) => st.qtyToPick).reduce((acc, qty) => acc + qty, 0);

        return [area.areaId, availableQtyInArea - reservedQtyInArea];
      });
      return [child.partId, Object.fromEntries(availableQtyPerArea)];
    });
    setAvailableQtyInAreaPerChild(Object.fromEntries(childAreaSummaryEntries));
  }, [part, selectedStUnitsPerChild, stUnitsPerChild, areas]);

  // Update plannedPickPerChild
  useEffect(() => {
    const hasUpdatedState = part.partId && part.children.every((ch) => !!availableQtyInAreaPerChild[ch.child.partId]);
    if (!hasUpdatedState) {
      return;
    }

    const plannedPickSummaryEntries = part.children.map(({ childQty, child }) => {
      const totalQtyToPick = childQty * qtyToAssemble;

      const selectedStUnits = selectedStUnitsPerChild[child.partId] ?? [];
      const qtyToPickFromStUnits = selectedStUnits.map((st) => st.qtyToPick).reduce((acc, qty) => acc + qty, 0);

      const remainingQtyToPick = totalQtyToPick - qtyToPickFromStUnits;
      const selectedAreaId = selectedAreaIdPerChild[child.partId];
      const availableQtyInArea = selectedAreaId ? availableQtyInAreaPerChild[child.partId][selectedAreaId] : null;
      const qtyToPickFromArea = availableQtyInArea ? Math.min(remainingQtyToPick, availableQtyInArea) : 0;

      const missingQty = remainingQtyToPick - qtyToPickFromArea;

      const plannedPickSummary = {
        totalQtyToPick,
        qtyToPickFromStUnits,
        qtyToPickFromArea,
        missingQty,
      };

      return [child.partId, plannedPickSummary];
    });
    setPlannedPickSummaryPerChild(Object.fromEntries(plannedPickSummaryEntries));
  }, [qtyToAssemble, part, selectedAreaIdPerChild, selectedStUnitsPerChild, availableQtyInAreaPerChild]);

  const handlePickStUnit = useCallback(
    (childPartId, stUnitId, qtyToPick) => {
      const stUnit = stUnitsPerChild[childPartId].find((st) => st.stUnitId == stUnitId);
      setSelectedStUnitsPerChild((prev) => {
        let selectedStUnits = prev[childPartId] ?? [];
        const index = selectedStUnits.findIndex((st) => st.stUnitId === stUnitId);
        if (index !== -1) {
          const existingSelectedStUnit = selectedStUnits[index];
          const updatedSelectedStUnit = {
            ...existingSelectedStUnit,
            qtyToPick: existingSelectedStUnit.qtyToPick + qtyToPick,
          };
          selectedStUnits = selectedStUnits.toSpliced(index, 1, updatedSelectedStUnit);
        } else {
          selectedStUnits = [...selectedStUnits, { ...stUnit, qtyToPick }];
        }
        return { ...prev, [childPartId]: selectedStUnits };
      });
      setShowSelectStUnitDialogForChild(null);
    },
    [stUnitsPerChild]
  );

  const handleClearStUnitPick = useCallback(
    (childPartId, stUnitId) =>
      setSelectedStUnitsPerChild((prev) => {
        const selectedStUnits = prev[childPartId];
        const index = selectedStUnits.findIndex((st) => st.stUnitId == stUnitId);
        if (index !== -1) {
          const updatedSelectedStUnits = selectedStUnits.toSpliced(index, 1);
          return { ...prev, [childPartId]: updatedSelectedStUnits };
        } else {
          return prev;
        }
      }),
    []
  );

  const handleSelectAreaForAll = useCallback(
    (areaId) => {
      const selectedAreas = Object.fromEntries(part.children.map((ch) => [ch.child.partId, areaId]));
      setSelectedAreaIdPerChild(selectedAreas);
    },
    [part]
  );

  const closeAndReload = useCallback(() => {
    //Soft reload with redirect
    const loc = encodeURIComponent(location.pathname);
    navigate(`/redirect/${loc}`, { replace: true });
  }, [navigate, location]);

  const handleSaveDialog = useCallback(async () => {
    let childPartPicks = [];

    for (let { child } of part.children) {
      const stUnitPicks = getStUnitPicksForChild(child, selectedStUnitsPerChild);
      childPartPicks = [...childPartPicks, ...stUnitPicks];

      const areaPicks = getAreaPicksForChild(
        child,
        stUnitsPerChild,
        selectedStUnitsPerChild,
        selectedAreaIdPerChild,
        plannedPickSummaryPerChild
      );
      childPartPicks = [...childPartPicks, ...areaPicks];
    }

    const request = {
      partId: part.partId,
      qty: qtyToAssemble,
      toPickAreaId: multiPartPickAreaId,
      childPartPicks: childPartPicks,
    };

    const response = await assembleMultiPart(request, api);
    if (response.isSuccessful) {
      if (onClose != null) {
        onClose();
      } else {
        closeAndReload();
      }
    }
  }, [
    api,
    stUnitsPerChild,
    selectedAreaIdPerChild,
    selectedStUnitsPerChild,
    plannedPickSummaryPerChild,
    qtyToAssemble,
    multiPartPickAreaId,
    part,
    closeAndReload,
    onSaved,
  ]);

  function getMultiPartDotMenuActions({ childQty, child }) {
    if (!plannedPickSummaryPerChild || Object.entries(plannedPickSummaryPerChild).length === 0) {
      return [];
    }

    return [
      {
        icon: <ViewInAr />,
        text: Translate.get("SelectSpecificPickArea"),
        disabled:
          plannedPickSummaryPerChild[child.partId].totalQtyToPick ==
          plannedPickSummaryPerChild[child.partId].qtyToPickFromStUnits,
        onClick: () => setShowSelectStUnitDialogForChild(child),
      },
    ];
  }

  const multiPartColumns = [
    {
      field: "partNo",
      flex: 0.3,
      headerName: Translate.get("PartNoLabel"),
      headerClassName: "hideSeparator",
      renderHeader: () => <IconAndLabelColumnHeader icon={Numbers} label={Translate.get("PartNoLabel")} />,
      valueGetter: (value, row, column, apiRef) => row.child?.partNo ?? "",
    },
    {
      field: "partId",
      flex: 0.6,
      headerName: Translate.get("PartLabel"),
      headerClassName: "hideSeparator",
      renderHeader: () => <IconAndLabelColumnHeader icon={ShoppingBasket} label={Translate.get("PartLabel")} />,
      valueGetter: (value, row, column, apiRef) => row.child?.description ?? "",
    },
    {
      field: "qty",
      flex: 0.25,
      headerName: Translate.get("NeedQty"),
      headerClassName: "hideSeparator",
      renderHeader: () => <IconAndLabelColumnHeader icon={ScatterPlot} label={Translate.get("NeedQty")} />,
      valueGetter: (value, row, column, apiRef) =>
        qtyToAssemble > 1 ? `${qtyToAssemble * row.childQty} (${qtyToAssemble}x${row.childQty})` : row.childQty,
    },
    {
      field: "totalAvailableQty",
      flex: 0.3,
      headerName: Translate.get("AvailableQtyTotal"),
      headerClassName: "hideSeparator",
      renderHeader: () => <IconAndLabelColumnHeader icon={ScatterPlot} label={Translate.get("AvailableQtyTotal")} />,
      valueGetter: (value, row, column, apiRef) => {
        const availableQtyInAreas = availableQtyInAreaPerChild[row.child.partId];
        return availableQtyInAreas
          ? Object.entries(availableQtyInAreas)
              .map(([_, qty]) => qty)
              .reduce((acc, qty) => acc + qty, 0)
          : 0;
      },
    },
    {
      field: "selectArea",
      flex: 0.5,
      headerName: Translate.get("MultiPartAutoPickArea"),
      headerClassName: "hideSeparator",
      renderHeader: () => <IconAndLabelColumnHeader icon={CropFree} label={Translate.get("MultiPartAutoPickArea")} />,
      renderCell: (params) => (
        <InnerSelectFromList
          list={areas.map((a) => {
            const availableQtyInArea = availableQtyInAreaPerChild[params.row.child.partId]
              ? availableQtyInAreaPerChild[params.row.child.partId][a.areaId] ?? 0
              : "??";
            return {
              ...a,
              shortNameWithQty: `${a.shortName} (${availableQtyInArea})`,
            };
          })}
          itemLabelName="shortNameWithQty"
          itemValueName="areaId"
          size="small"
          value={selectedAreaIdPerChild[params.row.child.partId]}
          handleUpdateValue={(newValue) =>
            setSelectedAreaIdPerChild((prev) => ({ ...prev, [params.row.child.partId]: newValue }))
          }
        />
      ),
    },
    {
      field: "dotMenu",
      flex: 0.1,
      headerName: "",
      sortable: false,
      disableColumnMenu: true,
      filterable: false,
      headerClassName: "hideSeparator",
      align: "right",
      hideable: false,
      renderCell: (params) =>
        getMultiPartDotMenuActions && <DotMenu content={getMultiPartDotMenuActions(params.row)} horizontal />,
    },
  ];

  const selectedAreaEntries = Object.entries(selectedAreaIdPerChild);
  const firstSelectedAreaId = selectedAreaEntries.length > 0 ? selectedAreaEntries[0][1] : null;
  const hasSelectedSameAreaForAll = selectedAreaEntries.every(([_, areaId]) => areaId == firstSelectedAreaId);

  // Sortera parts efter partNo
  if (part.children && Array.isArray(part.children)) {
    part.children = [...part.children].sort((a, b) => {
      const partNoA = a.child?.partNo ?? "";
      const partNoB = b.child?.partNo ?? "";
      return partNoA.localeCompare(partNoB, undefined, { numeric: true, sensitivity: "base" });
    });
  }

  return (
    <>
      {isLoading && <Preloader />}
      {showSelectStUnitDialogForChild && (
        <SelectMultiPartChildStUnitDialog
          childPart={showSelectStUnitDialogForChild}
          stUnits={stUnitsPerChild[showSelectStUnitDialogForChild.partId]}
          maxQtyToPick={
            plannedPickSummaryPerChild[showSelectStUnitDialogForChild.partId].totalQtyToPick -
            plannedPickSummaryPerChild[showSelectStUnitDialogForChild.partId].qtyToPickFromStUnits
          }
          alreadySelectedStUnitPicks={selectedStUnitsPerChild[showSelectStUnitDialogForChild.partId] ?? []}
          onPickStUnit={(stUnitId, qtyToPick) =>
            handlePickStUnit(showSelectStUnitDialogForChild.partId, stUnitId, qtyToPick)
          }
          onClose={() => setShowSelectStUnitDialogForChild(null)}
        />
      )}
      <DialogTitle>{`${Translate.get("AssembleMultiPartLabel")}: ${part.description}`}</DialogTitle>
      <DialogContent>
        {!isLoading && (
          <Grid container spacing={5} paddingTop={1}>
            <Grid container spacing={2} size={12}>
              <Grid size={12}>
                <h4>{Translate.get("MultiPartLabel")}</h4>
              </Grid>
              <Grid size={6}>
                <InnerTextField
                  required
                  labelKey="CreateQty"
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    type: "number",
                    min: 1,
                  }}
                  value={qtyToAssemble}
                  handleUpdateValue={(newValue) => setQtyToAssemble(newValue)}
                />
              </Grid>
              <Grid size={6}>
                <InnerSelectFromList
                  list={areas}
                  labelKey="SelectPickAreaForMultiPart"
                  itemLabelName="shortName"
                  itemValueName="areaId"
                  value={multiPartPickAreaId}
                  handleUpdateValue={(newValue) => setMultiPartPickAreaId(newValue)}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} size={12}>
              <Grid size={12}>
                <h4>{Translate.get("MultiPartChildrenTitle")}</h4>
              </Grid>
              <Grid size={6}>
                <InnerSelectFromList
                  list={areas}
                  labelKey="SelectPickAreaForAllChildren"
                  itemLabelName="shortName"
                  itemValueName="areaId"
                  value={hasSelectedSameAreaForAll ? firstSelectedAreaId : null}
                  handleUpdateValue={(newValue) => handleSelectAreaForAll(newValue)}
                />
              </Grid>
              <Grid size={12}>
                <DataGrid
                  showToolbar
                  apiRef={apiRef}
                  className={"dgDynIconText dgSmall"}
                  rows={part.children ?? []}
                  columns={multiPartColumns}
                  disableRowSelectionOnClick
                  getRowId={(row) => row.child.partId}
                  autoHeight={true}
                  slots={{
                    toolbar: () => ToolBar({ showExport: true, apiRef }),
                    noRowsOverlay: CustomNoRowsOverlay(
                      Translate.get("AddChildrenForMultiPart"),
                      Translate.get("NoChildren")
                    ),
                  }}
                  slotProps={{
                    columnsPanel: { className: "customColumnPanel" },
                    filterPanel: { className: "customfilterPanel" },
                    panel: { className: "customPanel" },
                  }}
                  localeText={localizedTextsMap}
                  disableColumnMenu
                  disableDensitySelector={true}
                  hideFooter={true}
                />
              </Grid>
            </Grid>

            <Grid container size={12}>
              <Accordion className="assembleMultiPartDialogAccordion" disableGutters defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMore />} aria-controls="content" id="header">
                  <Stack direction="row" spacing={2} alignItems="center">
                    <h4>{Translate.get("Summary")}</h4>
                    {Object.entries(plannedPickSummaryPerChild).some(([_, summary]) => summary.missingQty > 0) && (
                      <Box className="redWarning">
                        <Warning />
                      </Box>
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid size={12}>
                      {Translate.get("AssembleMultiPartChildrenSummaryInfo")}
                    </Grid>
                    <Grid container spacing={3} size={12}>
                      {part.children &&
                        Object.entries(plannedPickSummaryPerChild).length > 0 &&
                        part.children.map(({ childQty, child }) => (
                          <ChildSummary
                            key={child.partId}
                            childQty={childQty}
                            child={child}
                            selectedStUnits={selectedStUnitsPerChild[child.partId]}
                            selectedArea={
                              selectedAreaIdPerChild[child.partId]
                                ? areas.find((a) => a.areaId == selectedAreaIdPerChild[child.partId])
                                : null
                            }
                            plannedPickSummary={plannedPickSummaryPerChild[child.partId]}
                            onClearStUnitPick={(stUnitId) => handleClearStUnitPick(child.partId, stUnitId)}
                          />
                        ))}
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => (onClose ? onClose() : window.history.back())}>{Translate.get("Cancel")}</Button>
        <ButtonWithSpinner
          onClick={handleSaveDialog}
          disabled={Object.entries(plannedPickSummaryPerChild).some(([_, summary]) => summary.missingQty > 0)}
        >
          {Translate.get("Create")}
        </ButtonWithSpinner>
      </DialogActions>
      {errorMessage && (
        <AlertDialog
          bodyText={errorMessage}
          buttonText={Translate.get("Ok")}
          open
          onClose={() => setErrorMessage(null)}
        />
      )}
    </>
  );
}

function ChildSummary({ childQty, child, selectedStUnits, selectedArea, plannedPickSummary, onClearStUnitPick }) {
  return (
    <Grid container size={12}>
      <Grid size={12}>
        <Typography
          noWrap
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >{`${child.partNo} ${child.description}`}</Typography>
      </Grid>
      <Grid container className="assembleMultiPartSummaryChildContainer" size={12}>
        {!!selectedStUnits &&
          selectedStUnits.length > 0 &&
          selectedStUnits.map((st) => (
            <ChildSummaryRow key={"stunit_" + st.stUnitId} stUnitPick={st} onClearStUnitPick={onClearStUnitPick} />
          ))}

        {selectedArea && !!plannedPickSummary.qtyToPickFromArea && (
          <ChildSummaryRow
            areaPick={{
              qtyToPick: plannedPickSummary.qtyToPickFromArea,
              shortName: selectedArea.shortName,
            }}
          />
        )}

        {!!plannedPickSummary.missingQty && <ChildSummaryRow missingQty={plannedPickSummary.missingQty} />}
      </Grid>
    </Grid>
  );
}

function ChildSummaryRow({ areaPick, stUnitPick, missingQty, onClearStUnitPick }) {
  return (
    <Grid container className="assembleMultiPartSummaryChildRow" size={12}>
      <Grid size={4}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Inventory />
          {!!stUnitPick && <StUnitField {...stUnitPick} small />}
          {!!areaPick && (
            <Tooltip title={Translate.get("AssembleMultiPartChildrenSummaryAreaPickInfo")} arrow>
              <Box className="iconbox">{Translate.get("Automatic")}</Box>
            </Tooltip>
          )}
          {!!missingQty && (
            <Tooltip title={Translate.get("AssembleMultiPartChildrenSummaryMissingQtyInfo")} arrow>
              <Box className="iconbox warning">{Translate.get("MissingQtyLabel")}</Box>
            </Tooltip>
          )}
        </Stack>
      </Grid>
      <Grid size={4}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ScatterPlot />
          {!!stUnitPick && <Box>{stUnitPick.qtyToPick}</Box>}
          {!!areaPick && <Box>{areaPick.qtyToPick}</Box>}
          {!!missingQty && <Box>{missingQty}</Box>}
        </Stack>
      </Grid>
      <Grid size={!!stUnitPick ? 3 : 4}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CropFree />
          {!!stUnitPick && <Box>{stUnitPick.locationAreaName}</Box>}
          {!!areaPick && <Box>{areaPick.shortName}</Box>}
          {!!missingQty && <Box>??</Box>}
        </Stack>
      </Grid>
      {!!stUnitPick && (
        <Grid size={1}>
          <Box display="flex" justifyContent="flex-end" paddingRight="6px">
            <IconButton onClick={() => onClearStUnitPick(stUnitPick.stUnitId)}>
              <Clear />
            </IconButton>
          </Box>
        </Grid>
      )}
    </Grid>
  );
}

function InnerTextField({ labelKey, inputProps, value, handleUpdateValue, required }) {
  return (
    <TextField
      required={required}
      fullWidth
      value={value ?? ""}
      InputProps={{ spellCheck: false }}
      onChange={(event) => handleUpdateValue(event.target.value)}
      label={Translate.get(labelKey)}
      inputProps={inputProps}
    />
  );
}

function InnerSelect({ id, labelKey, items, value, handleUpdateValue, disabledFn, hideFn, size }) {
  let sortedItems = items ? [...items].sort((a, b) => a.label.localeCompare(b.label)) : items;
  return (
    <FormControl fullWidth size={size}>
      <InputLabel id={id}>{labelKey ? Translate.get(labelKey) : null}</InputLabel>
      <Select
        labelId={id}
        id={id + "select"}
        value={value ?? ""}
        label={labelKey ? Translate.get(labelKey) : null}
        onChange={(event) => handleUpdateValue(event.target.value)}
      >
        {sortedItems &&
          sortedItems
            .filter((item) => (hideFn ? !hideFn(item.value) : true))
            .map((item, index) => (
              <MenuItem key={index} value={item.value} disabled={disabledFn ? disabledFn(item.value) : false}>
                {item.label}
              </MenuItem>
            ))}
      </Select>
    </FormControl>
  );
}

function InnerSelectFromList({
  id,
  labelKey,
  list,
  itemLabelName,
  itemValueName,
  value,
  handleUpdateValue,
  compareFn,
  size,
}) {
  const items = list.map((item) => ({ label: item[itemLabelName], value: item[itemValueName] }));
  return (
    <InnerSelect
      id={id}
      labelKey={labelKey}
      items={items}
      value={value}
      handleUpdateValue={handleUpdateValue}
      compareFn={compareFn}
      size={size}
    />
  );
}

async function loadAreas(api) {
  const url = `${import.meta.env.VITE_MAIN_URL}warehouse/areas`;
  const response = await api.fetch(url, false, "GET");
  if (response.isSuccessful) {
    return response.value;
  } else {
    return null;
  }
}

async function loadPart(partId, api) {
  if (!partId) return null;

  const url = `${import.meta.env.VITE_MAIN_URL}warehouse/parts/${partId}`;
  const response = await api.fetch(url, false, "GET");
  return response.isSuccessful ? response : null;
}

async function assembleMultiPart(request, api) {
  const url = `${import.meta.env.VITE_MAIN_URL}warehouse/parts/multi/assemble`;
  const response = await api.fetch(url, request, "POST");
  return response;
}

function getStUnitPicksForChild(child, selectedStUnitsPerChild) {
  const selectedStUnits = selectedStUnitsPerChild[child.partId] ?? [];
  const stUnitPicks = selectedStUnits.map((st) => ({
    partId: child.partId,
    stUnitId: st.stUnitId,
    qty: st.qtyToPick,
  }));
  return stUnitPicks;
}

function getAreaPicksForChild(
  child,
  stUnitsPerChild,
  selectedStUnitsPerChild,
  selectedAreaIdPerChild,
  plannedPickSummaryPerChild
) {
  let qtyToPickFromArea = plannedPickSummaryPerChild[child.partId]?.qtyToPickFromArea;
  if (!qtyToPickFromArea) {
    return [];
  }

  const selectedAreaId = selectedAreaIdPerChild[child.partId];
  if (!selectedAreaId) {
    throw new Error("Missing selected areaId for child");
  }

  const stUnits = stUnitsPerChild[child.partId] ?? [];
  const stUnitsInArea = stUnits.filter((st) => st.locationAreaId == selectedAreaId);
  const selectedStUnits = selectedStUnitsPerChild[child.partId] ?? [];
  let areaPicks = [];

  for (let stUnitInArea of stUnitsInArea) {
    if (qtyToPickFromArea === 0) break;
    if (qtyToPickFromArea < 0) throw new Error("Picking more than intended");
    if (!qtyToPickFromArea) throw new Error("Invalid qty");

    const alreadyPickedStUnit = selectedStUnits.find((st) => st.stUnitId === stUnitInArea.stUnitId);
    const availableQty = stUnitInArea.qty - (alreadyPickedStUnit?.qtyToPick ?? 0);
    if (availableQty > 0) {
      const qtyToPick = Math.min(qtyToPickFromArea, availableQty);
      areaPicks.push({ partId: child.partId, stUnitId: stUnitInArea.stUnitId, qty: qtyToPick });
      qtyToPickFromArea -= qtyToPick;
    }
  }

  if (qtyToPickFromArea !== 0) throw new Error("Failed to pick from area");

  return areaPicks;
}
